import { FC } from 'react';
import {
  components,
  ControlProps,
  GroupBase,
  GroupHeadingProps,
  IndicatorsContainerProps,
  OptionProps,
  SingleValueProps,
} from 'react-select';
import { Box, Divider, HStack, Spacer, Text, VStack } from '@chakra-ui/react';

import { DEFAULT_ILLUSTRATION_NAME } from 'common/bankAccount/constants';
import { formatAmount } from 'common/formatAmount';
import IconCircle from 'components/_core/IconCircle';
import SunshineIcon from 'components/_core/SunshineIcon';
import Tooltip from 'components/_core/Tooltip';

import BankAccountIcon from '../BankAccountIcon';
import { StackedBankAccountIcons } from '../StackedBankAccountIcons';
import { ALL_ACCOUNTS_VALUE, NEW_ACCOUNT_VALUE } from './constants';
import locales from './locales';
import { OptionData } from './types';

const getBottomIllustrationName = (options: GroupBase<OptionData>[]) => {
  for (const group of options) {
    for (const option of group.options) {
      if (option.illustrationName !== DEFAULT_ILLUSTRATION_NAME) {
        return option.illustrationName;
      }
    }
  }
  return undefined;
};

export const SingleValueComponent = ({
  children,
  ...props
}: SingleValueProps<OptionData>) => {
  const isNewAccount = props.data.value === NEW_ACCOUNT_VALUE;
  const textColor = isNewAccount ? 'grey.500' : 'grey.800';

  return (
    <components.SingleValue {...props}>
      <Text
        color={textColor}
        fontSize="font-15"
        fontWeight="weight-500"
        noOfLines={1}
      >
        {props.data.name}
      </Text>
    </components.SingleValue>
  );
};

const NewAccountIcon = ({ isDisabled }: { isDisabled: boolean }) => (
  <IconCircle
    backgroundColor={isDisabled ? 'grey.200' : 'yellow.600'}
    color={isDisabled ? 'grey.500' : 'grey.800'}
    name="plus"
    size={24}
  />
);

interface NewAccountComponentProps extends GroupHeadingProps<OptionData> {
  disabled: boolean;
  tooltip?: string | null;
  display: boolean;
}

export const NewAccountComponent: FC<NewAccountComponentProps> = ({
  disabled,
  display,
  onClick,
  tooltip,
  ...props
}) => {
  if (!display) {
    return null;
  }

  return (
    <Box cursor={disabled ? 'not-allowed' : 'pointer'} onClick={onClick}>
      <components.GroupHeading {...props}>
        <Tooltip
          closeOnClick={false}
          description={tooltip}
          isDisabled={!tooltip}
        >
          <HStack direction="row" spacing="space-12">
            <NewAccountIcon isDisabled={disabled} />
            <VStack align="flex-start" spacing={0}>
              <Text
                fontSize="font-15"
                fontWeight="weight-500"
                variant={disabled ? 'disabled' : 'primary'}
              >
                {locales.createAssociatedAccount}
              </Text>
            </VStack>
          </HStack>
        </Tooltip>
      </components.GroupHeading>
      <Divider marginY={8} />
    </Box>
  );
};

export const OptionComponent = (props: OptionProps<OptionData>) => {
  const {
    data: { currency, currentBalance, illustrationName, name },
    isSelected,
    options,
  } = props;

  const amount = formatAmount({
    currency,
    forcePlusSign: false,
    value: currentBalance / 100,
  });

  return (
    <components.Option {...props}>
      <HStack direction="row" spacing="space-12">
        {props.data.value === ALL_ACCOUNTS_VALUE ? (
          <StackedBankAccountIcons
            bottomIllustrationName={getBottomIllustrationName(
              options as GroupBase<OptionData>[],
            )}
            size="big"
          />
        ) : (
          <BankAccountIcon illustrationName={illustrationName} size="md" />
        )}
        <VStack align="flex-start" spacing={0}>
          <Text
            color="grey.800"
            fontSize="font-15"
            fontWeight="weight-500"
            noOfLines={1}
          >
            {name}
          </Text>
          <Text color="grey.700" fontSize="font-13" fontWeight="weight-400">
            {amount}
          </Text>
        </VStack>
        <Spacer />
        <SunshineIcon
          color={isSelected ? 'primary' : 'grey.600'}
          name={isSelected ? 'radio-checked' : 'radio-unchecked'}
          size="icon-20"
        />
      </HStack>
    </components.Option>
  );
};

export const ControlComponent = ({
  children,
  ...props
}: ControlProps<OptionData>) => {
  const [firstOption] = props.getValue();
  const { illustrationName, value } = firstOption ?? {};

  let icon = (
    <BankAccountIcon
      illustrationName={illustrationName ?? DEFAULT_ILLUSTRATION_NAME}
      size="xs"
    />
  );

  if (value === NEW_ACCOUNT_VALUE) {
    icon = <NewAccountIcon isDisabled={props.isDisabled} />;
  }

  if (value === ALL_ACCOUNTS_VALUE) {
    const bottomIllustrationName = getBottomIllustrationName(
      props.options as GroupBase<OptionData>[],
    );

    icon = (
      <StackedBankAccountIcons
        bottomIllustrationName={bottomIllustrationName}
      />
    );
  }

  return (
    <components.Control {...props}>
      <HStack cursor="pointer" direction="row" h="40px" spacing={0}>
        {icon}
        {children}
      </HStack>
    </components.Control>
  );
};

export const IndicatorsContainerComponent = (
  props: IndicatorsContainerProps<OptionData>,
) => (
  <components.IndicatorsContainer {...props}>
    <SunshineIcon name="chevron-down" size="icon-20" />
  </components.IndicatorsContainer>
);
